import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FileText, Search, Settings, X, Zap } from 'lucide-react';
import { Spinner } from '../common/Spinner';
import type { SearchOptions } from '../../types';

interface SearchPanelProps {
  onSearch: (benchmark: string, options: SearchOptions) => void;
  isSearching: boolean;
  hasSearched: boolean;
}

// 常用数据集列表
const QUICK_SEARCH_DATASETS = [
  { name: 'ImageNet', category: 'CV' },
  { name: 'COCO', category: 'CV' },
  { name: 'CIFAR', category: 'CV' },
  { name: 'MNIST', category: 'CV' },
  { name: 'CelebA', category: 'CV' },
  { name: 'GLUE', category: 'NLP' },
  { name: 'SQuAD', category: 'NLP' },
];

export const SearchPanel = ({ onSearch, isSearching, hasSearched }: SearchPanelProps) => {
  const [benchmark, setBenchmark] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [options, setOptions] = useState<SearchOptions>({
    limit: 10,
    sortBy: 'citation_desc',
    maxWorkers: 5
  });

  const handleSearch = () => {
    if (benchmark.trim()) {
      onSearch(benchmark.trim(), options);
    }
  };

  const handleQuickSearch = (dataset: string) => {
    setBenchmark(dataset);
    onSearch(dataset, options);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isSearching) {
      handleSearch();
    }
  };

  return (
    <div className="w-full relative">
      {/* 搜索框容器 */}
      <div className={hasSearched ? '' : 'max-w-2xl mx-auto'}>
        {/* 搜索框 */}
        <div
          className={`
            relative flex items-center bg-white rounded-2xl border-2
            ${isSearching ? 'border-gray-200' : 'border-gray-200 hover:border-gray-300'}
            shadow-sm hover:shadow-md transition-all duration-200
          `}
        >
          <div className="pl-4 text-gray-400">
            <Search size={20} />
          </div>
          <input
            type="text"
            value={benchmark}
            onChange={(e) => setBenchmark(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isSearching}
            placeholder="输入数据集名称（如 ImageNet、GLUE、SQuAD）"
            autoComplete="off"
            style={{ outline: 'none', boxShadow: 'none' }}
            className="
              flex-1 px-4 py-3 text-gray-800 placeholder-gray-400
              bg-transparent border-none
              focus:outline-none focus:ring-0 focus:border-transparent
              disabled:cursor-not-allowed text-sm
            "
          />
          {benchmark && !isSearching && (
            <button
              onClick={() => setBenchmark('')}
              className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X size={16} />
            </button>
          )}
          {/* 高级选项按钮 */}
          <button
            onClick={() => setShowAdvanced(!showAdvanced)}
            className={`
              p-2 mr-1 rounded-lg transition-colors
              ${showAdvanced ? 'text-[#94070a] bg-red-50' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'}
            `}
            title="高级选项"
          >
            <Settings size={18} />
          </button>
          <button
            onClick={handleSearch}
            disabled={isSearching || !benchmark.trim()}
            className={`
              m-1.5 px-5 py-2 rounded-xl font-medium text-sm
              flex items-center gap-2 transition-all duration-200
              ${isSearching || !benchmark.trim()
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-[#94070a] text-white hover:bg-[#7a0608] shadow-md hover:shadow-lg'
              }
            `}
          >
            {isSearching ? (
              <>
                <Spinner size={16} className="text-white" />
                搜索中...
              </>
            ) : (
              <>
                <Search size={16} />
                搜索
              </>
            )}
          </button>
        </div>

        {/* 快速搜索标签 - 仅在首页显示 */}
        {!hasSearched && !isSearching && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mt-4 flex flex-wrap items-center gap-2 justify-center"
          >
            <span className="text-xs text-gray-400 flex items-center gap-1">
              <Zap size={12} />
              快速搜索:
            </span>
            {QUICK_SEARCH_DATASETS.map((dataset) => (
              <button
                key={dataset.name}
                onClick={() => handleQuickSearch(dataset.name)}
                className="
                  px-3 py-1.5 text-xs font-medium
                  bg-white border border-gray-200 rounded-full
                  text-gray-600 hover:text-[#94070a] hover:border-[#94070a] hover:bg-red-50
                  transition-all duration-200
                  shadow-sm hover:shadow
                "
              >
                {dataset.name}
              </button>
            ))}
          </motion.div>
        )}

        {/* 高级选项下拉面板 */}
        <AnimatePresence>
          {showAdvanced && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
              className="absolute z-50 left-0 right-0 mt-2"
            >
              <div className="p-4 bg-white rounded-xl border border-gray-200 shadow-lg">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-gray-700">高级选项</span>
                  <button
                    onClick={() => setShowAdvanced(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X size={16} />
                  </button>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  {/* 论文数量 */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1.5">
                      论文数量
                    </label>
                    <select
                      value={options.limit}
                      onChange={(e) => setOptions({ ...options, limit: parseInt(e.target.value) })}
                      className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#94070a] focus:border-[#94070a] outline-none"
                    >
                      <option value={5}>5篇</option>
                      <option value={10}>10篇</option>
                      <option value={15}>15篇</option>
                      <option value={20}>20篇</option>
                    </select>
                  </div>

                  {/* 排序方式 */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1.5">
                      排序方式
                    </label>
                    <select
                      value={options.sortBy}
                      onChange={(e) => setOptions({ ...options, sortBy: e.target.value })}
                      className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#94070a] focus:border-[#94070a] outline-none"
                    >
                      <option value="citation_desc">引用数（高→低）</option>
                      <option value="citation_asc">引用数（低→高）</option>
                      <option value="year_desc">年份（新→旧）</option>
                      <option value="year_asc">年份（旧→新）</option>
                    </select>
                  </div>

                  {/* 并发数 */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1.5">
                      分析并发数
                    </label>
                    <select
                      value={options.maxWorkers}
                      onChange={(e) => setOptions({ ...options, maxWorkers: parseInt(e.target.value) })}
                      className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#94070a] focus:border-[#94070a] outline-none"
                    >
                      <option value={1}>1（最稳定）</option>
                      <option value={3}>3</option>
                      <option value={5}>5（推荐）</option>
                    </select>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

// Logo组件 - 搜索前大Logo，搜索后小Logo
interface LogoProps {
  hasSearched: boolean;
}

export const Logo = ({ hasSearched }: LogoProps) => {
  return (
    <AnimatePresence mode="wait">
      {!hasSearched ? (
        // 搜索前：大Logo居中
        <motion.div
          key="hero-logo"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.3 }}
          className="text-center mb-8"
        >
          <div className="flex items-center justify-center gap-4 mb-4">
            <div className="w-16 h-16 bg-[#94070a] rounded-2xl flex items-center justify-center shadow-lg">
              <FileText className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-4xl font-bold text-gray-900 tracking-tight">
                北大 Benchmark论文追踪
              </h1>
              <p className="text-[#94070a] text-sm font-medium mt-1">北京大学 · 智能学术分析工具</p>
            </div>
          </div>
          <p className="text-gray-500 text-lg">
            输入数据集名称，自动获取相关论文并对比分析
          </p>
        </motion.div>
      ) : (
        // 搜索后：小Logo置顶
        <motion.div
          key="compact-logo"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3 }}
          className="flex items-center gap-2"
        >
          <div className="w-9 h-9 bg-[#94070a] rounded-lg flex items-center justify-center shadow-sm">
            <FileText className="w-5 h-5 text-white" />
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export type { SearchOptions };
